import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authService } from '@gtintel/signin/services/auth.service';
import { ApiError } from '@gtintel/signin/types/api.types';
import { LoginRequest, LoginResponse, User } from '@gtintel/signin/types/auth.types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  redirectUrls: { nexus: string; platform: string } | null;
  userEmail: string | null;
  login: (data: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  setRedirectUrls: (urls: { nexus: string; platform: string }) => void;
  setUserEmail: (email: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrls, setRedirectUrlsState] = useState<{
    nexus: string;
    platform: string;
  } | null>(null);
  const [userEmail, setUserEmailState] = useState<string | null>(null);
  const navigate = useNavigate();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const login = useCallback(async (data: LoginRequest): Promise<LoginResponse> => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (data.source_url) {
        // If source_url is provided, use login endpoint for single system auth
        response = await authService.login(data);
      } else {
        // For direct login, use signin endpoint for dual system auth
        response = await authService.signin(data);
      }
      return response;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setRedirectUrlsState(null);
      setUserEmailState(null);
      navigate('/login');
    } catch (err) {
      // Logout failed, but we still navigate to login
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const setRedirectUrls = useCallback((urls: { nexus: string; platform: string }) => {
    setRedirectUrlsState(urls);
  }, []);

  const setUserEmail = useCallback((email: string) => {
    setUserEmailState(email);
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    error,
    redirectUrls,
    userEmail,
    login,
    logout,
    clearError,
    setRedirectUrls,
    setUserEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
